import React from "react";
import { IProgressIndicatorProps } from "containers/OnboardingBusiness/types";

const ProgressIndicator: React.FC<IProgressIndicatorProps> = ({
  currentStep,
  totalSteps,
}) => {
  const steps = [
    { number: "01", label: "Basic Details", mobileLabel: "Basic Details" },
    {
      number: "02",
      label: "Additional Information",
      mobileLabel: "Additional Info",
    },
    { number: "03", label: "Business Photos", mobileLabel: "Add Photos" },
  ];

  return (
    <div className="w-full">
      {/* Mobile View - Horizontal with labels and progress bars */}
      <div className="md:hidden">
        {/* Step Labels */}
        <div className="flex items-center justify-between mb-3 gap-1">
          {steps.map((step, index) => {
            const isActive = currentStep === index + 1;
            const isCompleted = currentStep > index + 1;

            return (
              <div
                key={index}
                className="flex items-center gap-1 flex-1 min-w-0"
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted
                      ? "bg-[var(--purple)]"
                      : isActive
                        ? "bg-[#6B46C1]"
                        : "bg-gray-400"
                  }`}
                >
                  <svg
                    className="w-2.5 h-2.5 sm:w-3 sm:h-3"
                    fill="white"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span
                  className={`text-[10px] sm:text-xs font-medium leading-tight truncate ${
                    isCompleted
                      ? "text-[var(--purple)]"
                      : isActive
                        ? "text-[#6B46C1]"
                        : "text-gray-400"
                  }`}
                >
                  {step.mobileLabel}
                </span>
                {index < steps.length - 1 && (
                  <div className="mx-0.5 text-gray-300 text-[10px] flex-shrink-0">
                    •
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Progress Bars */}
        <div className="flex gap-2">
          {steps.map((step, index) => {
            const isCompleted = currentStep > index + 1;
            const isActive = currentStep === index + 1;

            return (
              <div
                key={index}
                className="flex-1 h-1 bg-[#E5E5EA] rounded-full overflow-hidden"
              >
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    isCompleted
                      ? "bg-[var(--purple)]"
                      : isActive
                        ? "bg-[#6B46C1]"
                        : "bg-[#E5E5EA]"
                  }`}
                  style={{ width: currentStep >= index + 1 ? "100%" : "0%" }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop View - Full stepper with all labels */}
      <div className="hidden md:flex relative items-center justify-end py-3 pr-4 gap-[10px]">
        {steps.map((step, index) => {
          const isCompleted = currentStep > index + 1;
          const isActive = currentStep === index + 1;

          return (
            <React.Fragment key={index}>
              <div className="relative z-10 flex items-center gap-[10px]">
                <div
                  className={`rounded-full flex items-center justify-center text-sm font-medium ${
                    isCompleted
                      ? "w-7 h-7 bg-[var(--purple)] text-white border-0"
                      : isActive
                        ? "w-9 h-9 bg-white text-[#0D28A6] border-[1.5px] border-[#0D28A6] p-2"
                        : "w-9 h-9 bg-white text-gray-400 border-[1.5px] border-gray-300 p-2"
                  }`}
                >
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="white" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    step.number
                  )}
                </div>
                <div
                  className={`font-poppins font-medium text-base leading-none whitespace-nowrap ${
                    isCompleted
                      ? "text-[var(--purple)]"
                      : isActive
                        ? "text-[#0D28A6]"
                        : "text-gray-400"
                  }`}
                >
                  {step.label}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-8 h-0.5 ${
                    isCompleted
                      ? "bg-[var(--purple)]"
                      : isActive
                        ? "bg-[#0D28A6]"
                        : "bg-gray-300"
                  }`}
                ></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressIndicator;
